import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IShelter from '../../interfaces/IShelter'
import { SearchRequest, ShelterSaveRequest, ShelterCreateRequest, ShelterVerifySlugRequest, ShelterToggleActiveRequest, ShelterQuestionUpdateRequest, ShelterPaymentConfigSaveRequest, ShelterKioskLogoUpdateRequest, ShelterActivateInPersonRequest } from '../../services/api/interfaces/ApiRequest'
import { shelterList, shelterSave, shelterCreate, shelterVerifySlug, shelterToggleActive, shelterQuestionUpdate, shelterPaymentConfigSave, shelterKioskLogoUpdate, shelterActivateInPerson } from '../../services/api'

interface ShelterContextState extends BaseContextState {
  shelters: Array<IShelter> | null,
  shelter: IShelter | null,
  validSlug: boolean | null
}

export interface ShelterContextData extends ShelterContextState {
  resetErrors: Function,
  requestShelterList: (args: SearchRequest) => Promise<any>,
  requestShelterFind: (args: SearchRequest) => Promise<any>,
  requestShelterSave: (args: ShelterSaveRequest) => Promise<any>,
  requestShelterCreate: (args: ShelterCreateRequest) => Promise<any>,
  requestShelterVerifySlug: (args: ShelterVerifySlugRequest) => Promise<any>,
  requestShelterToggleActive: (args: ShelterToggleActiveRequest) => Promise<any>,
  requestShelterQuestionUpdate: (args: ShelterQuestionUpdateRequest) => Promise<any>,
  requestShelterPaymentConfigSave: (args: ShelterPaymentConfigSaveRequest) => void
  requestShelterKioskLogoUpdate: (args: ShelterKioskLogoUpdateRequest) => Promise<any>,
  requestShelterActivateInPerson: (args: ShelterActivateInPersonRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as ShelterContextData)

// Export consumer
export const ShelterConsumer = Consumer

export class ShelterProvider extends BaseContextProvider {
  state: ShelterContextState = {
    shelters: null,
    shelter: null,
    validSlug: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for shelter list
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterList = (payload: SearchRequest) => {

    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        shelter: null
      }, async () => {
        const response = await shelterList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'shelters'
          },
        })

        if (this.state.success) {
          resolve(this.state.shelters)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter finder
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterFind = (payload: SearchRequest) => {
    return new Promise((resolve) => {
      this.setState({
        fetching: true,
        error: null,
        shelter: null
      }, async () => {
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        this.lastListPayload = payload
        const response = await shelterList(payload)

        const responseData = (response as any)?.data?.data

        if (responseData && Array.isArray(responseData.result)) {
          (response as any).data.data = responseData.result[0] || null
        }

        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'shelter'
          },
        })

        resolve(true)
      })
    })
  }

  /**
   * Make api request for shelter save
   * 
   * @param {ShelterSaveRequest} payload 
   */
  requestShelterSave = (payload: ShelterSaveRequest) => {
    return new Promise((resolve) => {
      this.setState({
        fetching: true,
        error: null,
        //shelter: null // For now we cannot loses this reference!!!
      }, async () => {
        const response = await shelterSave(payload)
  
        // Process our response
        this.processResponse(response)

        resolve(true)
      })
    })
  }

  /**
   * Make api request for shelter create
   * 
   * @param {ShelterCreateRequest} payload 
   */
   requestShelterCreate = (payload: ShelterCreateRequest) => {
    return new Promise((resolve) => {
      this.setState({
        fetching: true,
        error: null,
        shelter: null
      }, async () => {
        const response = await shelterCreate(payload)
  
        // Process our response
        this.processResponse(response, {
          mappings: {
            origin: 'organization_shelter', destination: 'shelter'
          }
        })

        resolve(true)
      })
    })
  }

  /**
   * Make api request for shelter verify slug
   * 
   * @param {ShelterVerifySlugRequest} payload 
   */
   requestShelterVerifySlug = (payload: ShelterVerifySlugRequest) => {
    return new Promise((resolve) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterVerifySlug(payload)
  
        // Process our response
        this.processResponse(response, {
          mappings: {
            origin: 'valid_slug', destination: 'validSlug'
          }
        })

        resolve(this.state.validSlug)
      })
    })
  }

  /**
   * Make api request for shelter toggle active
   * 
   * @param {ShelterToggleActiveRequest} payload 
   */
   requestShelterToggleActive= (payload: ShelterToggleActiveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterToggleActive(payload)
  
        // Process our response
        this.processResponse(response)

        if (this.state.success) {
          this.requestShelterList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter update questions
   * 
   * @param {ShelterQuestionUpdateRequest} payload 
   */
  requestShelterQuestionUpdate = (payload: ShelterQuestionUpdateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        //shelter: null // For now we cannot loses this reference!!!
      }, async () => {
        const response = await shelterQuestionUpdate(payload)
  
        // Process our response
        this.processResponse(response)

        if (this.state.success) {
          resolve(this.state.shelter)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter payment config save
   * 
   * @param {ShelterPaymentConfigSaveRequest} payload 
   */
  requestShelterPaymentConfigSave = (payload: ShelterPaymentConfigSaveRequest) => {
    this.setState({
      fetching: true,
      error: null,
      //shelter: null // For now we cannot loses this reference!!!
    }, async () => {
      const response = await shelterPaymentConfigSave(payload)

      // Process our response
      this.processResponse(response)
    })
  }

  /**
   * Make api request for shelter kiosk logo update
   * 
   * @param {ShelterKioskLogoUpdateRequest} payload 
   */
   requestShelterKioskLogoUpdate = (payload: ShelterKioskLogoUpdateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterKioskLogoUpdate(payload)
  
        // Process our response
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterFind(this.lastListPayload || {})

          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter activate in person
   * 
   * @param {ShelterActivateInPersonRequest} payload 
   */
   requestShelterActivateInPerson = (payload: ShelterActivateInPersonRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterActivateInPerson(payload)
  
        // Process our response
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          setTimeout(async () => {
            await this.requestShelterFind(this.lastListPayload || {})

            resolve(true)
          }, 2000)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestShelterList: this.requestShelterList,
          requestShelterFind: this.requestShelterFind,
          requestShelterSave: this.requestShelterSave,
          requestShelterCreate: this.requestShelterCreate,
          requestShelterVerifySlug: this.requestShelterVerifySlug,
          requestShelterToggleActive: this.requestShelterToggleActive,
          requestShelterQuestionUpdate: this.requestShelterQuestionUpdate,
          requestShelterPaymentConfigSave: this.requestShelterPaymentConfigSave,
          requestShelterKioskLogoUpdate: this.requestShelterKioskLogoUpdate,
          requestShelterActivateInPerson: this.requestShelterActivateInPerson
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
